import qs from 'qs';
import { type ILayerAttribute } from '../../metadata/LayerMetadata.ts';
import { type IMapLayerWfsOptions } from '../MapLayer.ts';
import { parseLayerAndOptions } from '../rendering/layerUrl.ts';
import { WfsDefaultVersion } from './buildWfsGetFeatureUrl.ts';

/** Descrizione di un feature type ottenuta da DescribeFeatureType. */
export interface IWfsFeatureTypeDescription {
  /** Attributi alfanumerici (il campo geometria è escluso) */
  fields: ILayerAttribute[];
  geometryFieldName?: string;
  /** Tipo di geometria GML (es. 'MultiSurface', 'Point') */
  geometryType?: string;
}

/** URL della richiesta DescribeFeatureType per un singolo feature type. */
export function buildWfsDescribeFeatureTypeUrl(
  rawUrl: string,
  wfs: IMapLayerWfsOptions,
): string {
  const { url, options: existingParams } = parseLayerAndOptions(rawUrl);
  const version = wfs.version ?? WfsDefaultVersion;
  const params = {
    ...existingParams,
    service: 'WFS',
    request: 'DescribeFeatureType',
    version,
    // WFS 2.0.0 ha rinominato il parametro `typeName` in `typeNames`
    [version === '2.0.0' ? 'typeNames' : 'typeName']: wfs.typeName,
  };
  return `${url}?${qs.stringify(params)}`;
}

/** Rimuove l'eventuale prefisso di namespace ('osm:water_areas' → 'water_areas'). */
function stripNamespacePrefix(value: string): string {
  const colonIndex = value.indexOf(':');
  return colonIndex >= 0 ? value.substring(colonIndex + 1) : value;
}

function isGmlPropertyType(type: string | undefined): boolean {
  return type?.startsWith('gml:') ?? false;
}

/** 'gml:MultiSurfacePropertyType' → 'MultiSurface' */
function extractGeometryType(type: string): string {
  return stripNamespacePrefix(type).replace(/PropertyType$/, '');
}

function findFeatureTypeComplexType(
  root: Element,
  typeName: string,
): Element | undefined {
  const complexTypes = Array.from(
    root.getElementsByTagNameNS('*', 'complexType'),
  );
  const localTypeName = stripNamespacePrefix(typeName);

  // Convenzione XSD dei servizi WFS: il complexType del feature type
  // 'prefix:nome' si chiama 'nomeType'
  const match = complexTypes.find(
    complexType => complexType.getAttribute('name') === `${localTypeName}Type`,
  );

  // Con un solo complexType nel documento (risposta a un DescribeFeatureType
  // con typeName singolo) il fallback è comunque univoco
  return match ?? complexTypes[0];
}

/**
 * Interpreta lo schema XSD restituito da DescribeFeatureType estraendo
 * gli attributi del feature type richiesto. Gli elementi con tipo `gml:*`
 * sono la geometria e vengono esclusi dagli attributi.
 *
 * @throws Error se il documento non è uno schema XSD o non contiene il
 *   feature type richiesto.
 */
export function parseWfsDescribeFeatureType(
  xml: string,
  typeName: string,
): IWfsFeatureTypeDescription {
  const document = new DOMParser().parseFromString(xml, 'text/xml');

  const parserError = document.getElementsByTagName('parsererror')[0];
  const root = document.documentElement;
  if (parserError || !root || root.localName !== 'schema') {
    throw new Error('La risposta non è un DescribeFeatureType WFS valido');
  }

  const complexType = findFeatureTypeComplexType(root, typeName);
  if (!complexType) {
    throw new Error(
      `Feature type '${typeName}' non trovato nella risposta DescribeFeatureType`,
    );
  }

  const description: IWfsFeatureTypeDescription = { fields: [] };

  for (const element of Array.from(
    complexType.getElementsByTagNameNS('*', 'element'),
  )) {
    const name = element.getAttribute('name');
    if (!name) continue;

    const type = element.getAttribute('type') ?? undefined;

    if (isGmlPropertyType(type)) {
      description.geometryFieldName = name;
      description.geometryType = extractGeometryType(type as string);
      continue;
    }

    description.fields.push({
      name,
      type: type ? stripNamespacePrefix(type) : undefined,
    });
  }

  return description;
}
